import create from 'zustand'
import { combine } from 'zustand/middleware'
import { ScenePosition } from '../hooks/useScenePositionDrag'

/** Selection-mode key while the user picks the reference and origin points on the floorplan. */
export const STOREY_IMAGE_ORIGIN_SELECTION_KEY = 'storey-image-origin'

/** Snackbar message for the first pick — the fixed target point the plan gets moved to. */
export const STOREY_IMAGE_REFERENCE_SELECTION_MESSAGE =
  'Referenzpunkt wählen (Ziel der Verschiebung)'

/** Snackbar message for the second pick — the point on the plan that gets moved onto the reference. */
export const STOREY_IMAGE_ORIGIN_SELECTION_MESSAGE = 'Neuen Ursprungspunkt auf dem Plan wählen'

interface StoreyImageOriginState {
  /** Storey whose floorplan image the origin tool is active for, or null when inactive. */
  activeStorey: string | null
  /** First picked world-space point — the fixed target the plan gets moved to. */
  referencePoint: ScenePosition | null
  /** Second picked world-space point — the point on the plan that gets moved onto the reference. */
  origin: ScenePosition | null
  /** Gizmo x-axis angle (rad) relative to the global X axis. */
  rotation: number
}

const initialState: StoreyImageOriginState = {
  activeStorey: null,
  referencePoint: null,
  origin: null,
  rotation: 0,
}

/**
 * Transient (non-persisted) state of the "In Szene" floorplan-image origin tool, shared between
 * the storey-image controls (which start the tool and apply the result) and the
 * `StoreyImageOriginControl` scene element (which picks and drags the origin point). Living in a
 * store keeps the picked point stable across scene-element remounts.
 */
const useStoreyImageOriginStore = create(
  combine(initialState, set => ({
    startTool: (storey: string) =>
      set({ activeStorey: storey, referencePoint: null, origin: null, rotation: 0 }),
    setReferencePoint: (referencePoint: ScenePosition) => set({ referencePoint }),
    setOrigin: (origin: ScenePosition) => set({ origin }),
    setRotation: (rotation: number) => set({ rotation }),
    reset: () => set({ activeStorey: null, referencePoint: null, origin: null, rotation: 0 }),
  })),
)

export default useStoreyImageOriginStore
